import type { Canvas, FabricObject, TMat2D } from 'fabric';

// A textbox keeps the width it was given: a word too long for it is drawn past its own edges,
// so its bounding box says nothing about what is on screen. These two say what is really
// drawn.
type TMeasurableText = FabricObject & { calcTextHeight?: () => number; calcTextWidth?: () => number };

type TViewportSceneServiceParams = {
    canvas: Canvas;
    getCanvasHeight: () => number;
    getCanvasWidth: () => number;
    // Zoom of the default view, derived by the scene from the canvas and
    // surface sizes. Read through a getter so a resize is picked up.
    getDefaultZoom: () => number;
    getImageRenderHeight: () => number;
    getImageRenderWidth: () => number;
};

const FOCUS_ELEMENT_SCALE_RATIO = 0.5;
const FOCUS_ELEMENT_MAX_SCALE = 0.7;
const MOVE_DURATION_MS = 350;

// How the view travels to its new framing. `fillRatio` and `maxScale` say how much of the
// canvas the focused element takes and how far the zoom may go — the defaults are what a
// resize has always done. `animate` slides the view there instead of jumping, so the
// customer sees where they have been taken; `onUpdate` fires on every frame, which is what
// keeps the zoom readout and a field's contextual buttons on the move with it.
// `verticalBias`: pixels to lift the framing by, so a text stays in the part of the canvas
// the on-screen keyboard leaves visible rather than behind it.
// `visibleWidth` / `visibleHeight`: the band that is actually on screen. Fitting to the canvas
// instead — most of which sits behind the keyboard, the bars or below the fold — is what made
// a text that had been enlarged zoom past the edges of the screen.
type TFocusOptions = {
    animate?: boolean;
    fillRatio?: number;
    maxScale?: number;
    onUpdate?: () => void;
    verticalBias?: number;
    visibleHeight?: number;
    visibleWidth?: number;
};

class ViewportSceneService {
    private readonly _canvas: Canvas;
    private readonly _getCanvasHeight: () => number;
    private readonly _getCanvasWidth: () => number;
    private readonly _getDefaultZoom: () => number;
    private readonly _getImageRenderHeight: () => number;
    private readonly _getImageRenderWidth: () => number;
    private _animationFrame: number | null = null;

    constructor(params: TViewportSceneServiceParams) {
        this._canvas = params.canvas;
        this._getCanvasHeight = params.getCanvasHeight;
        this._getCanvasWidth = params.getCanvasWidth;
        this._getDefaultZoom = params.getDefaultZoom;
        this._getImageRenderHeight = params.getImageRenderHeight;
        this._getImageRenderWidth = params.getImageRenderWidth;
    }

    displayImageFullCanvas(options: TFocusOptions = {}): void {
        const zoom = this._getDefaultZoom();
        const imageRenderWidth = this._getImageRenderWidth();
        const imageRenderHeight = this._getImageRenderHeight();
        const canvasWidth = this._getCanvasWidth();
        const canvasHeight = this._getCanvasHeight();

        this._applyTransform(
            [zoom, 0, 0, zoom, canvasWidth / 2 - (imageRenderWidth / 2) * zoom, canvasHeight / 2 - (imageRenderHeight / 2) * zoom],
            options,
        );
    }

    focusOnElement(element: FabricObject, options: TFocusOptions = {}): void {
        const { fillRatio = FOCUS_ELEMENT_SCALE_RATIO, maxScale = FOCUS_ELEMENT_MAX_SCALE } = options;
        const { x, y } = element.getCenterPoint();
        const bBox = element.getBoundingRect();
        const drawn = this._drawnSize(element);
        const elementWidth = Math.max(bBox.width, drawn.width);
        const elementHeight = Math.max(bBox.height, drawn.height);
        const canvasWidth = this._getCanvasWidth();
        const canvasHeight = this._getCanvasHeight();
        const fitWidth = options.visibleWidth ?? canvasWidth;
        const fitHeight = options.visibleHeight ?? canvasHeight;
        // The binding dimension is the one that runs out first, which is a matter of ratios:
        // comparing the element's own width and height ignores the shape of the space it has
        // to fit into.
        const isWidthBinding = elementWidth / fitWidth > elementHeight / fitHeight;
        const calcScalefactor = (isWidthBinding ? fitWidth / elementWidth : fitHeight / elementHeight) * fillRatio;
        const scaleFactor = Math.min(calcScalefactor, maxScale);

        const bias = options.verticalBias ?? 0;
        this._applyTransform(
            [scaleFactor, 0, 0, scaleFactor, canvasWidth / 2 - x * scaleFactor, (canvasHeight - bias) / 2 - y * scaleFactor],
            options,
        );
    }

    private _drawnSize(element: FabricObject): { height: number; width: number } {
        const text = element as TMeasurableText;
        if (typeof text.calcTextWidth !== 'function' || typeof text.calcTextHeight !== 'function') {
            return { height: 0, width: 0 };
        }
        return {
            height: text.calcTextHeight() * (element.scaleY ?? 1),
            width: text.calcTextWidth() * (element.scaleX ?? 1),
        };
    }

    private _applyTransform(target: TMat2D, options: TFocusOptions): void {
        this._stopAnimation();
        if (!options.animate || this._prefersReducedMotion()) {
            this._canvas.setViewportTransform(target);
            this._canvas.renderAll();
            options.onUpdate?.();
            return;
        }

        const start = [...this._canvas.viewportTransform] as TMat2D;
        const startedAt = performance.now();
        const step = () => {
            const progress = Math.min(1, (performance.now() - startedAt) / MOVE_DURATION_MS);
            // Ease out: the view leaves quickly and settles, which reads as one movement
            // rather than a jump.
            const eased = 1 - Math.pow(1 - progress, 3);
            this._canvas.setViewportTransform(start.map((value, index) => value + (target[index] - value) * eased) as TMat2D);
            this._canvas.renderAll();
            options.onUpdate?.();
            this._animationFrame = progress < 1 ? requestAnimationFrame(step) : null;
        };
        this._animationFrame = requestAnimationFrame(step);
    }

    private _stopAnimation(): void {
        if (this._animationFrame !== null) {
            cancelAnimationFrame(this._animationFrame);
            this._animationFrame = null;
        }
    }

    private _prefersReducedMotion(): boolean {
        return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    }
}

export { ViewportSceneService };

export type { TFocusOptions };
